/**
 * Точка входа.
 *
 * Экран входа стоит перед маршрутизацией: пока сессии нет, никаких маршрутов
 * не существует — иначе адрес задачи вёл бы на пустую оболочку.
 */

import * as React from "react";
import { createRoot } from "react-dom/client";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { RouterProvider } from "@tanstack/react-router";

import "./app.css";

import { ErrorBoundary } from "@/app/ErrorBoundary";
import { router } from "@/app/router";
import { LoginPage } from "@/modules/auth/LoginPage";
import { onReconnect } from "@/shared/lib/reconnect";
import { useSession } from "@/shared/lib/session";
import { SkeletonRows } from "@/shared/ui/Feedback";
import { TooltipProvider } from "@/shared/ui/Overlays";
import { ConfirmProvider } from "@/shared/ui/Confirm";
import { ToastProvider } from "@/shared/ui/Toast";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Данные системы управления работой меняются людьми, а не сами:
      // минута свежести избавляет от лишних запросов при переключении вкладок
      staleTime: 60_000,
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

function App(): React.ReactElement {
  const status = useSession((state) => state.status);
  const restore = useSession((state) => state.restore);

  React.useEffect(() => {
    void restore();
  }, [restore]);

  // Пока связь была потеряна, изменения коллег до вкладки не дошли. После
  // восстановления открытые экраны перечитываются: иначе человек продолжал бы
  // работать с картиной мира, устаревшей на время обрыва
  React.useEffect(
    () =>
      onReconnect(() => {
        void queryClient.invalidateQueries();
      }),
    [],
  );

  if (status === "unknown") {
    // Скелетон, а не спиннер: показываем структуру будущего экрана
    return (
      <div className="min-h-screen bg-[var(--pt-bg-canvas)] p-[var(--pt-space-24)]">
        <SkeletonRows count={6} />
      </div>
    );
  }

  if (status === "anonymous") {
    return <LoginPage />;
  }

  return <RouterProvider router={router} />;
}

const container = document.getElementById("root");
if (!container) {
  throw new Error("Не найден корневой элемент #root");
}

createRoot(container).render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <ToastProvider>
          <ConfirmProvider>
            <ErrorBoundary>
              <App />
            </ErrorBoundary>
          </ConfirmProvider>
        </ToastProvider>
      </TooltipProvider>
    </QueryClientProvider>
  </React.StrictMode>,
);
